import { Autocomplete, Box, Button, Divider, OutlinedInput, Stack, TextField, Typography } from "@mui/material";
import React from "react";
import Modal, { CommonModalProps } from "..";
import { DbFileInfos } from "../../../Core/Api/Types/db-file-infos";
import MetadataBoxAccordion from "../../Misc/Metadata/MetadataBoxAccordion";
import MetadataListTextItem from "../../Misc/Metadata/MetadataListTextItem/metadata-list-text-item";
import moment from "moment";
import useCustomSnackbar from "../../../Utils/Hooks/useCustomSnackbar";
import { DbFileUserDataHistoryInfos } from "../../../Core/Api/Types/db-file-user-data-history-infos";
import AddIcon from "@mui/icons-material/Add";
import { mediaService } from "../../../Core/Api/services";
import { AddFileUserDataRequest } from "../../../Core/Api/Types/add-file-user-data-request";
import { UserDataStatus } from "../../../Core/Api/Types/user-data-status";
import { UserDataType } from "../../../Core/Api/Types/user-data-type";
import GlobalConst from "../../../Core/Utils/Constants/globals";
import FormSection from "../../Forms/FormSection/form-section";
import { useTranslation } from "react-i18next";

export type ModalFileUserDataHistoryElementProps = {
  onClose?: (props: any) => void;
  file: DbFileInfos;
  data: DbFileUserDataHistoryInfos | null;
};

export type ModalFileUserDataHistoryProps = CommonModalProps<ModalFileUserDataHistoryElementProps>;

const ModalFileUserDataHistory = React.forwardRef<any, ModalFileUserDataHistoryProps>((props, ref) => {
  const { t } = useTranslation();
  const { closeModal, onClose, file, data, ...elementProps } = props;
  const [fileStepType, setFileStepType] = React.useState<UserDataType>();
  const [fileStepStatus, setFileStepStatus] = React.useState<UserDataStatus>();
  const [fileStepMessage, setFileStepMessage] = React.useState<string>();
  const customSnackbar = useCustomSnackbar();

  const handleAddFileStep = () => {
    mediaService
      .AddUserDataOnMediaFile(
        new AddFileUserDataRequest({
          fileId: file.id,
          status: fileStepStatus,
          type: fileStepType,
          message: fileStepMessage,
        })
      )
      .then(function (response) {
        if (response.success) {
          onClose && onClose({ confirm: true });
          closeModal();
          customSnackbar(t("alert:success.newFileStepAdded"), "success");
        } else customSnackbar(t("alert:error.oopsWithMessage", { message: response.message }), "warning");
      });
  };

  return (
    <Modal
      title={
        <Typography sx={{ wordBreak: "break-all" }} variant="h5">
          {t("modal:title.userDataHistory")} - {file.name}
        </Typography>
      }
      closeModal={closeModal}
      width={500}
      reference={ref}
      footer={
        <Stack direction="row" justifyContent="flex-end" spacing={1}>
          <Button variant="outlined" onClick={closeModal}>
            {t("common:close")}
          </Button>
        </Stack>
      }
    >
      <Stack spacing={2}>
        {data !== null && (
          <Stack spacing={1}>
            <MetadataListTextItem title={t("common:date")}>
              {moment(data.time).format(GlobalConst.MOMENT_DATE_FORMAT_FULL)}
            </MetadataListTextItem>
            <MetadataListTextItem title={t("common:type")}>{data.type}</MetadataListTextItem>
            <MetadataListTextItem title={t("common:status")}>{file.status}</MetadataListTextItem>
            <MetadataListTextItem title={t("common:user")}>
              {data.userName === null ? t("common:system") : data.userName}
            </MetadataListTextItem>
            <MetadataListTextItem title={t("common:message")}>
              {data.message === null ? "-" : data.message}
            </MetadataListTextItem>
          </Stack>
        )}
        {data === null && <Stack spacing={1}>{t("modal:modalFileUserDataHistory.body.noUserDataOnThisFile")}</Stack>}

        <Divider sx={{ mt: 2, mb: 2 }} />

        <FormSection
          title={t("form:section.title.addNewfileStep")}
          statusText={t("field:helper.defineNewStatus")}
          status="details"
          size="small"
        >
          <Autocomplete
            sx={{ mt: -3 }}
            options={GlobalConst.userDataTypes}
            onChange={(e, value) => setFileStepType(value?.value)}
            getOptionLabel={(value: any) => value.label}
            renderInput={(params) => <TextField {...params} placeholder={t("field:placeholder.selectType")} />}
          />
          <Autocomplete
            options={GlobalConst.userDataStatuses}
            onChange={(e, value) => setFileStepStatus(value?.value)}
            getOptionLabel={(value: any) => value.label}
            renderInput={(params) => <TextField {...params} placeholder={t("field:placeholder.selectStatus")} />}
          />
          <OutlinedInput
            sx={{ mt: 3 }}
            onChange={(e) => setFileStepMessage(e.target.value)}
            placeholder={t("field:placeholder.leaveMessageHere")}
          />

          <Stack direction="row" justifyContent="flex-end" spacing={1} mt={2}>
            <Button
              color="success"
              disabled={fileStepStatus === null || fileStepMessage === null || fileStepMessage === ""}
              onClick={handleAddFileStep}
            >
              {t("common:add")}
            </Button>
          </Stack>
        </FormSection>
      </Stack>
    </Modal>
  );
});

export default ModalFileUserDataHistory;
